import { targetInfoType } from "../target/Types";
import { getParams } from "../services";
import { getTablesKey } from "../services/target";
import { DB_PROVIDERS } from "../../../../app/components/appwizard/component-wizards/wizards/source/database-reader/configs/db-providers";
import componentService, { ComponentKeys } from "../../../../core/services/componentService/component-service";
import ComponentNames from "../../../../resources/generated/components.json";

const VALIDATION_TIMEOUT = 2_000;

const validationUtils = {
    getTargetType: function(target: string): string {
        switch (target) {
            case "OutputOracleDatabase":
                return DB_PROVIDERS.ORACLE.name;
            case "OutputPostgresDatabase":
            case "CloudSqlPostgreSqlWriter":
            case "AzurePostgreSqlWriter":
                return DB_PROVIDERS.POSTGRES.name;
            case "OutputMySQLDatabase":
            case "CloudSqlMySqlWriter":
                return DB_PROVIDERS.MYSQL.name;
            case "OutputSQLServerDatabase":
                return "MSSQL";
            case "MariaDBDatabase":
                return DB_PROVIDERS.MARIADB.name;
            default:
                return target;
        }
    },
    isAppAdapter: function(adapterName: string): boolean {
        if (!adapterName) {
            return false;
        }
        const sourceName = componentService.getHumanReadableName(adapterName)?.toUpperCase();

        if (DB_PROVIDERS?.[sourceName]) {
            return !!DB_PROVIDERS?.[sourceName]?.isAppAdapter;
        }
        return (
            Object.values(DB_PROVIDERS).find(
                adapterData => adapterData?.name?.toUpperCase() === sourceName?.toUpperCase()
            )?.isAppAdapter ?? false
        );
    },
    isDWH: function(adapterName: string): boolean {
        const DWHList = [
            "RedshiftWriter",
            "SnowflakeWriter",
            "AzureSQLDWHWriter",
            "FabricDataWarehouseWriter",
            "BigQuery",
            "DeltaLakeWriter",
            "HiveWriter",
            "IcebergWriter",
            "FabricMirrorWriter",
            "S3TablesWriter",
            "ClickHouseWriter"
        ];
        return DWHList.includes(adapterName);
    },
    isRDBMS: function(adapterName: string): boolean {
        const RDBMSList = [
            "MARIADB",
            "MARIADBXPAND",
            "SQLSERVER",
            "MSSQL",
            "MYSQL",
            "POSTGRES",
            "ORACLE",
            "YUGABYTEDB",
            "YUGABYTECDC"
        ];
        return RDBMSList.includes(adapterName.toUpperCase());
    },
    isCPAvailableForOLTP: function(adapterName: string): boolean {
        const OLTPEndpoints = [
            DB_PROVIDERS.ORACLE.id,
            DB_PROVIDERS.OJET.id,
            DB_PROVIDERS.POSTGRES.id,
            DB_PROVIDERS.SQLSERVER.id,
            DB_PROVIDERS.MSJET.id,
            DB_PROVIDERS.MYSQL.id,
            DB_PROVIDERS.MARIADB.id,
            DB_PROVIDERS.MARIADBXPAND.id,
            "OJETCDC",
            "MSSQL"
        ];
        return OLTPEndpoints.includes(adapterName?.toUpperCase());
    },
    getRDBMSAdvancedParams: function(connectionParams): Object {
        return {
            CDDLAction: connectionParams?.CDDLAction,
            CDDLTrackingTable: connectionParams?.CDDLTrackingTable,
            ParallelThreads: connectionParams?.ParallelThreads
        };
    },

    // ClickHouse needs the table-mapping string for its privilege/connection checks. Copies it onto the params under the adapter's Tables key.
    addTablesParam: function(target: string, targetInfo: targetInfoType, params: Record<string, unknown>) {
        if (target === ComponentNames.ClickHouseWriter[ComponentKeys.Adapter]?.toUpperCase()) {
            const propertyTemplateID =
                "Global.PROPERTYTEMPLATE." + ComponentNames.ClickHouseWriter[ComponentKeys.Adapter];
            const tablesKey = getTablesKey(propertyTemplateID);
            const defaultProperties = targetInfo?.defaultProperties ?? {};
            const tables = tablesKey ? defaultProperties[tablesKey] : undefined;
            if (!tablesKey || !tables) {
                throw new Error("ClickHouse table mapping is missing; cannot validate the target.");
            }
            params[tablesKey] = tables;
        }
        return params;
    },

    getParams: async function(target: string, targetInfo: targetInfoType) {
        const connectionParams: targetInfoType["targetFormValues"] = { ...targetInfo.targetFormValues };
        const formatProperties = {
            username: "userName",
            connectionUrl: "URL"
        };
        if (validationUtils.isRDBMS(target)) {
            const connParams = await getParams(target, connectionParams, false, true);
            const advancedParams = validationUtils.getRDBMSAdvancedParams(connectionParams);
            return { ...connParams, ...advancedParams };
        } else {
            for (const key in formatProperties) {
                if (connectionParams.hasOwnProperty(key)) {
                    connectionParams[formatProperties[key]] = connectionParams[key];
                    delete connectionParams[key];
                }
            }
            if (connectionParams.hasOwnProperty("password")) {
                connectionParams["passwordEncrypted"] = false;
            }
            if (connectionParams.hasOwnProperty("catalog") && !connectionParams["catalog"]) {
                delete connectionParams["catalog"];
            }
            if (connectionParams.hasOwnProperty("formattedConnectionUrl")) {
                connectionParams["ConnectionURL"] = connectionParams["formattedConnectionUrl"];
                delete connectionParams["formattedConnectionUrl"];
            }
            connectionParams["streamingUpload"] =
                typeof connectionParams["streamingUpload"] === "boolean"
                    ? connectionParams["streamingUpload"]
                    : connectionParams["streamingUpload"] === "true";

            validationUtils.addTablesParam(target, targetInfo, connectionParams);

            return connectionParams;
        }
    },
    isAutomatedILCDCSource: source => {
        const automatedILCDCSources = [
            "ORACLE",
            "SQLSERVER",
            "MYSQL",
            "POSTGRES",
            "MARIADB",
            "MARIADBXPAND",
            "OJET",
            "MSJET",
            "SALESFORCE",
            "SALESFORCECDCREADER"
        ];
        return automatedILCDCSources.includes(source);
    },
    getSourceParams: connectionParams => {
        return {
            UserName: connectionParams["username"],
            Password: connectionParams["password"],
            ConnectionURL: connectionParams["connectionUrl"],
            encrypted: false,
            containerName: connectionParams["containerName"] || undefined,
            service: connectionParams["service"]
        };
    },
    replaceConnectionURL: (formInfo: targetInfoType): targetInfoType => {
        const targetFields = { ...formInfo?.targetFormValues };

        if (targetFields?.["formattedConnectionUrl"]) {
            targetFields["connectionUrl"] = targetFields["formattedConnectionUrl"];
            delete targetFields["formattedConnectionUrl"];
        }
        return { ...formInfo, targetFormValues: targetFields };
    }
};

export { validationUtils, VALIDATION_TIMEOUT };
